import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Select, SelectItem, Label, Textarea, DateInput } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { PlusIcon, TrashIcon, ChevronDownIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * ComplaintActionTakenIntegration - Component for managing complaint actions taken within the complaint form
 * Similar to ComplaintActionsIntegration but for ComplaintActionTaken
 */
const ComplaintActionTakenIntegration = ({ 
  complaintId,
  formData,
  setFormData,
  config,
  isEditMode = false
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localActionsTaken, setLocalActionsTaken] = useState([]);

  // Fetch lookup data
  const { data: actionTakenTypes = [], isLoading: actionTakenTypesLoading } = useFlexibleList(
    config?.api?.lookupEndpoints?.complaintActionTakenTypes ? config.api.lookupEndpoints.complaintActionTakenTypes : null
  );

  // Fetch existing actions taken if complaintId exists
  const { data: existingActionsTaken = [] } = useFlexibleList(
    complaintId && isEditMode ? `/complaint-action-taken/complaint/${complaintId}` : null
  );

  // Track if we've initialized to prevent infinite loops
  const isInitializedRef = React.useRef(false);
  const prevComplaintIdRef = React.useRef(complaintId);
  
  // Use ref to store latest actions taken for immediate access
  const localActionsTakenRef = React.useRef([]);

  // Initialize actions taken only once when component mounts or complaintId changes
  useEffect(() => {
    // Reset initialization flag when complaintId changes
    if (prevComplaintIdRef.current !== complaintId) {
      isInitializedRef.current = false;
      prevComplaintIdRef.current = complaintId;
    }

    // Only initialize if we haven't already done so
    if (isInitializedRef.current) return;

    // Priority: existingActionsTaken (from API) > formData.complaintActionTaken > empty array
    if (existingActionsTaken && Array.isArray(existingActionsTaken) && existingActionsTaken.length > 0) {
      // Load existing actions taken from API if available (for edit mode)
      setLocalActionsTaken(existingActionsTaken);
      isInitializedRef.current = true;
    } else {
      // For new records OR edit mode without existing actions, check formData first, then initialize empty array
      if (formData.complaintActionTaken && Array.isArray(formData.complaintActionTaken) && formData.complaintActionTaken.length > 0) {
        setLocalActionsTaken(formData.complaintActionTaken);
      } else {
        setLocalActionsTaken([]);
      }
      isInitializedRef.current = true;
    }
  }, [complaintId, isEditMode, existingActionsTaken, formData.complaintActionTaken]); // Added formData.complaintActionTaken back but with proper handling

  // Update ref whenever localActionsTaken changes
  useEffect(() => {
    localActionsTakenRef.current = localActionsTaken;
  }, [localActionsTaken]);

  // Sync localActionsTaken to formData (but only if we've initialized)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Sync immediately without timeout to ensure data is available when form is submitted
    const syncToFormData = () => {
      setFormData(prev => {
        // Only update if the data actually changed to prevent unnecessary re-renders
        const currentActionsTaken = prev.complaintActionTaken || [];
        const currentActionsTakenStr = JSON.stringify(currentActionsTaken);
        const localActionsTakenStr = JSON.stringify(localActionsTaken);
        
        if (currentActionsTakenStr === localActionsTakenStr) {
          return prev;
        }
        
        console.log('🔄 Syncing localActionsTaken to formData:', localActionsTaken);
        
        return {
          ...prev,
          complaintActionTaken: localActionsTaken
        };
      });
    };
    
    syncToFormData();
  }, [localActionsTaken, setFormData]);
  
  // Force sync before form submission - expose sync function via window for debugging
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__syncComplaintActionTaken = () => {
        // Always sync, even if not initialized - use current localActionsTaken state
        const latestActions = localActionsTakenRef.current.length > 0 
          ? localActionsTakenRef.current 
          : localActionsTaken;
        
        console.log('🔄 Force syncing complaintActionTaken to formData:', latestActions);
        console.log('🔄 isInitialized:', isInitializedRef.current);
        console.log('🔄 localActionsTaken length:', latestActions.length);
        
        // Use functional update to ensure we get the latest state
        setFormData(prev => {
          const updated = {
            ...prev,
            complaintActionTaken: latestActions
          };
          console.log('✅ Force sync complete - updated formData with complaintActionTaken:', updated.complaintActionTaken);
          // Update the ref as well to ensure consistency
          localActionsTakenRef.current = latestActions;
          return updated;
        });
      };
      
      // Also expose getter for debugging and direct data access
      window.__getComplaintActionTaken = () => {
        const latestActions = localActionsTakenRef.current.length > 0 
          ? localActionsTakenRef.current 
          : localActionsTaken;
        return {
          localActionsTaken: latestActions,
          localActionsTakenState: localActionsTaken,
          isInitialized: isInitializedRef.current,
          formData: formData.complaintActionTaken
        };
      };
    }
  }, [setFormData, formData, localActionsTaken]);

  const addActionTaken = () => {
    const newActionTaken = {
      id: `temp-${Date.now()}`, // Temporary ID for new actions taken
      actionTakenId: null,
      description: '',
      startedAt: null,
      isNew: true // Flag to identify new actions taken
    };
    setLocalActionsTaken(prev => {
      const updated = [...prev, newActionTaken];
      console.log('🔄 Adding action taken:', updated);
      // Update ref immediately for consistency
      localActionsTakenRef.current = updated;
      return updated;
    });
    setIsExpanded(true); // Auto-expand when adding new action taken
  };

  const removeActionTaken = (index) => {
    setLocalActionsTaken(prev => {
      const newActionsTaken = prev.filter((_, i) => i !== index);
      console.log('🔄 Removing action taken:', newActionsTaken);
      // Update ref immediately for consistency
      localActionsTakenRef.current = newActionsTaken;
      return newActionsTaken;
    });
  };

  const updateActionTaken = (index, field, value) => {
    setLocalActionsTaken(prev => {
      const newActionsTaken = [...prev];
      newActionsTaken[index] = {
        ...newActionsTaken[index],
        [field]: value
      };
      console.log('🔄 Updating action taken:', { index, field, value, newActionsTaken });
      // Update ref immediately for consistency
      localActionsTakenRef.current = newActionsTaken;
      return newActionsTaken;
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">✅</span>
          <span className="font-semibold">Actions Taken</span>
          {localActionsTaken.length > 0 && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              theme.colors.primary,
              theme.text.inverse
            )}>
              {localActionsTaken.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t", theme.border.light)}>
          {localActionsTaken.length === 0 ? (
            <div className={cn("text-center py-8", theme.text.secondary)}>
              <p className="mb-4">No actions taken added yet.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addActionTaken}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add First Action Taken
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {localActionsTaken.map((actionTaken, index) => (
                <div
                  key={actionTaken.id || index}
                  className={cn(
                    "p-4 rounded-lg border space-y-4",
                    theme.border.subtle,
                    theme.colors.backgroundAlt
                  )}
                >
                  {/* Action Taken Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-medium", theme.text.secondary)}>
                        Action Taken #{index + 1}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeActionTaken(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Action Taken Fields - Responsive Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
                    {/* Action Taken Type */}
                    <div className="sm:col-span-1 lg:col-span-3">
                      <Label htmlFor={`actionTakenType-${index}`}>
                        Action Taken Type
                      </Label>
                      <Select
                        id={`actionTakenType-${index}`}
                        value={actionTaken.actionTakenId?.toString() || ''}
                        onChange={(e) => updateActionTaken(index, 'actionTakenId', e.target.value ? parseInt(e.target.value) : null)}
                        disabled={actionTakenTypesLoading}
                      >
                        <SelectItem value="">Select action taken type</SelectItem>
                        {actionTakenTypes?.map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </Select>
                      {actionTakenTypesLoading && (
                        <div className={cn("text-xs mt-1", theme.text.secondary)}>
                          Loading action taken types...
                        </div>
                      )}
                    </div>

                    {/* Started At */}
                    <div className="sm:col-span-1 lg:col-span-4">
                      <Label htmlFor={`startedAt-${index}`}>
                        Started At
                      </Label>
                      <DateInput
                        id={`startedAt-${index}`}
                        value={formatDateForInput(actionTaken.startedAt)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateActionTaken(index, 'startedAt', dateValue);
                        }}
                      />
                    </div>

                    {/* Description - Full Width */}
                    <div className="sm:col-span-2 lg:col-span-12">
                      <Label htmlFor={`description-${index}`} required>
                        Description
                      </Label>
                      <Textarea
                        id={`description-${index}`}
                        value={actionTaken.description || ''}
                        onChange={(e) => updateActionTaken(index, 'description', e.target.value)}
                        placeholder="Enter action taken description"
                        rows={2}
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Action Taken Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addActionTaken}
                className="w-full"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Another Action Taken
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplaintActionTakenIntegration;

